import { dateFormatter } from './calendarFormatters';
/* global BigInt */
// Browser-owned calculation port; parity fixtures retain the original domain rules.

const CALCULATION_VERSION = 'account-cost-v1';

const REASONS = Object.freeze({
  MISSING_PROFILE: 'MISSING_EFFECTIVE_PROFILE',
  MISSING_TARIFF: 'MISSING_APPLICABLE_TARIFF',
  INPUTS_NOT_ALIGNED: 'ENERGY_INPUTS_NOT_ALIGNED',
  STALE_INPUT: 'STALE_ENERGY_INPUT',
  PARTIAL_COVERAGE: 'PARTIAL_ENERGY_COVERAGE',
  MISSING_ROLLUP: 'MISSING_REQUIRED_ROLLUP',
  ZERO_DENOMINATOR: 'ZERO_SERVED_ENERGY_DENOMINATOR',
  MISSING_MODELED_RATE: 'MISSING_MODELED_GENERATION_RATE',
  MISSING_ENERGY_INPUT: 'MISSING_REQUIRED_ENERGY_INPUT',
  MISSING_LCOE_INPUT: 'MISSING_MODELED_LCOE_INPUT',
  OUTAGE_NOT_SELECTED: 'OUTAGE_SCENARIO_NOT_SELECTED',
  OUTAGE_INPUT_REQUIRED: 'OUTAGE_EVENT_INPUT_REQUIRED',
  UNSUPPORTED_SCENARIO: 'UNSUPPORTED_COST_SCENARIO',
  AMBIGUOUS_ROLLUP: 'AMBIGUOUS_ROLLUP_SERIES',
  SOURCE_SERIES_UNAVAILABLE: 'ENERGY_SOURCE_SERIES_UNAVAILABLE',
  TARIFF_ELIGIBILITY_UNPROVEN: 'TARIFF_BUCKET_ELIGIBILITY_UNPROVEN',
  DEMAND_INTERVAL_UNPROVEN: 'DEMAND_INTERVAL_ELIGIBILITY_UNPROVEN',
  DEMAND_BILLING_PERIOD_UNPROVEN: 'DEMAND_BILLING_PERIOD_ELIGIBILITY_UNPROVEN',
  BILLING_START_DAY_MISMATCH: 'COST_PROFILE_SITE_BILLING_START_DAY_MISMATCH',
  FIXED_PERIOD_UNPROVEN: 'FIXED_PERIOD_SEMANTICS_UNPROVEN',
  LEASE_CONFIGURATION_INVALID: 'LEASE_CONFIGURATION_INVALID',
  LEASE_DENOMINATOR_UNAVAILABLE: 'LEASE_ENERGY_DENOMINATOR_UNAVAILABLE',
  LEASE_DENOMINATOR_CONFLICT: 'LEASE_ENERGY_DENOMINATOR_CONFLICT',
  LEASE_LCOE_UNSUPPORTED: 'LEASE_FINANCING_USES_ACCRUAL_COST',
  SITE_SCOPE_UNAVAILABLE: 'SITE_SCOPE_UNAVAILABLE',
  SITE_MODELED_GENERATION_UNAVAILABLE: 'SITE_MODELED_GENERATION_UNAVAILABLE',
  OUTAGE_SCENARIO_UNAVAILABLE: 'OUTAGE_SCENARIO_UNAVAILABLE',
  UNSERVED_ENERGY_UNAVAILABLE: 'UNSERVED_LOAD_ENERGY_UNAVAILABLE'
});

function expandExponent(value) {
  const text = String(value);
  if (!/[eE]/.test(text)) return text;
  const [coefficient, exponentText] = text.toLowerCase().split('e');
  const exponent = Number(exponentText);
  const negative = coefficient.startsWith('-');
  const unsigned = negative ? coefficient.slice(1) : coefficient;
  const [whole, fraction = ''] = unsigned.split('.');
  const digits = whole + fraction;
  const point = whole.length + exponent;
  let expanded;
  if (point <= 0) expanded = `0.${'0'.repeat(-point)}${digits}`;
  else if (point >= digits.length) expanded = `${digits}${'0'.repeat(point - digits.length)}`;
  else expanded = `${digits.slice(0, point)}.${digits.slice(point)}`;
  return `${negative ? '-' : ''}${expanded}`;
}

function decimal(value) {
  if (value && typeof value === 'object' && typeof value.n === 'bigint' && Number.isInteger(value.s)) return value;
  const text = expandExponent(value);
  if (!/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(text)) throw new TypeError(`Invalid decimal value: ${text}`);
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [whole, fraction = ''] = unsigned.split('.');
  return normalize({ n: BigInt(`${negative ? '-' : ''}${whole}${fraction}`), s: fraction.length });
}

function normalize(value) {
  let { n, s } = value;
  while (s > 0 && n % 10n === 0n) {
    n /= 10n;
    s -= 1;
  }
  return { n, s };
}

function scaleTo(value, scale) {
  return value.n * (10n ** BigInt(scale - value.s));
}

function add(left, right) {
  const a = decimal(left);
  const b = decimal(right);
  const scale = Math.max(a.s, b.s);
  return normalize({ n: scaleTo(a, scale) + scaleTo(b, scale), s: scale });
}

function subtract(left, right) {
  const b = decimal(right);
  return add(left, { n: -b.n, s: b.s });
}

function multiply(left, right) {
  const a = decimal(left);
  const b = decimal(right);
  return normalize({ n: a.n * b.n, s: a.s + b.s });
}

function divide(left, right, precision = 12) {
  const a = decimal(left);
  const b = decimal(right);
  if (b.n === 0n) throw new RangeError('Division by zero');
  const exponent = precision + b.s - a.s;
  let numerator = a.n;
  let denominator = b.n;
  if (exponent >= 0) numerator *= 10n ** BigInt(exponent);
  else denominator *= 10n ** BigInt(-exponent);
  let quotient = numerator / denominator;
  const remainder = numerator % denominator;
  if (remainder !== 0n && (remainder < 0n ? -remainder : remainder) * 2n >= (denominator < 0n ? -denominator : denominator)) {
    quotient += (numerator < 0n) !== (denominator < 0n) ? -1n : 1n;
  }
  return normalize({ n: quotient, s: precision });
}

function compare(left, right) {
  const a = decimal(left);
  const b = decimal(right);
  const scale = Math.max(a.s, b.s);
  const delta = scaleTo(a, scale) - scaleTo(b, scale);
  return delta === 0n ? 0 : (delta < 0n ? -1 : 1);
}

function fixed(value, places) {
  const input = decimal(value);
  let scaled;
  if (input.s <= places) scaled = input.n * (10n ** BigInt(places - input.s));
  else {
    const divisor = 10n ** BigInt(input.s - places);
    scaled = input.n / divisor;
    const remainder = input.n % divisor;
    if ((remainder < 0n ? -remainder : remainder) * 2n >= divisor) scaled += input.n < 0n ? -1n : 1n;
  }
  const negative = scaled < 0n;
  const digits = (negative ? -scaled : scaled).toString().padStart(places + 1, '0');
  if (places === 0) return `${negative ? '-' : ''}${digits}`;
  return `${negative ? '-' : ''}${digits.slice(0, -places)}.${digits.slice(-places)}`;
}

function sum(values) {
  return values.reduce((total, value) => add(total, value), decimal('0'));
}

function isDecimal(value) {
  try {
    decimal(value);
    return true;
  } catch (_) {
    return false;
  }
}

function unavailable(unit, reasonCodes) {
  return { value: null, unit, status: 'unavailable', reasonCodes: [...new Set([].concat(reasonCodes).filter(Boolean))] };
}

function localClock(instant, timezone) {
  const parts = dateFormatter('en-US', {
    timeZone: timezone, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(new Date(instant));
  const values = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
  const weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(values.year), month: Number(values.month), day: Number(values.day), weekday: weekdays[values.weekday],
    seconds: Number(values.hour) * 3600 + Number(values.minute) * 60 + Number(values.second)
  };
}

function timezoneOffsetMs(instant, timezone) {
  const local = localClock(instant, timezone);
  return Date.UTC(local.year, local.month - 1, local.day, Math.floor(local.seconds / 3600),
    Math.floor((local.seconds % 3600) / 60), local.seconds % 60) - Math.floor(new Date(instant).getTime() / 1000) * 1000;
}

function zonedLocalUtcCandidates(year, month, day, seconds, timezone) {
  const localEpoch = Date.UTC(year, month - 1, day) + seconds * 1000;
  const offsets = new Set();
  for (let delta = -259200000; delta <= 259200000; delta += 21600000) {
    offsets.add(timezoneOffsetMs(localEpoch + delta, timezone));
  }
  const candidates = [...offsets].map(offset => localEpoch - offset).filter(candidate => {
    const actual = localClock(candidate, timezone);
    return actual.year === year && actual.month === month && actual.day === day && actual.seconds === seconds;
  }).sort((a, b) => a - b);
  return [...new Set(candidates)];
}

function zonedMidnightUtc(year, month, day, timezone) {
  const candidates = zonedLocalUtcCandidates(year, month, day, 0, timezone);
  if (!candidates.length) throw new RangeError('Lease billing boundary does not exist in configured timezone');
  return candidates[0];
}

function calendarMonth(year, month, delta) {
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function parseDateParts(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
}

function leaseBoundary(month, anchorDay, timezone) {
  return zonedMidnightUtc(month.year, month.month, Math.min(anchorDay, daysInMonth(month.year, month.month)), timezone);
}

function leaseCycleAt(cost, instant, timezone) {
  const startParts = parseDateParts(cost.startDate);
  if (!startParts || cost.recurrence?.frequency !== 'monthly' || !isDecimal(cost.amount)) return null;
  const instantMs = Date.parse(instant);
  if (!Number.isFinite(instantMs)) return null;
  const leaseStart = zonedMidnightUtc(startParts.year, startParts.month, startParts.day, timezone);
  const endParts = parseDateParts(cost.recurrence?.endDate);
  const leaseEnd = endParts ? zonedMidnightUtc(endParts.year, endParts.month, endParts.day, timezone) : null;
  if (instantMs < leaseStart || (leaseEnd !== null && instantMs >= leaseEnd)) return null;
  const local = localClock(instantMs, timezone);
  let month = { year: local.year, month: local.month };
  let cycleStart = leaseBoundary(month, startParts.day, timezone);
  if (cycleStart > instantMs) {
    month = calendarMonth(month.year, month.month, -1);
    cycleStart = leaseBoundary(month, startParts.day, timezone);
  }
  const nextMonth = calendarMonth(month.year, month.month, 1);
  const cycleEnd = leaseBoundary(nextMonth, startParts.day, timezone);
  const cycleLocal = localClock(cycleStart, timezone);
  let completedYears = cycleLocal.year - startParts.year;
  if (cycleLocal.month < startParts.month || (cycleLocal.month === startParts.month && cycleLocal.day < startParts.day)) completedYears -= 1;
  completedYears = Math.max(completedYears, 0);
  let payment = decimal(cost.amount);
  const escalationBase = add('1', cost.escalationRate || '0');
  for (let year = 0; year < completedYears; year += 1) payment = multiply(payment, escalationBase);
  return {
    cycleStart: new Date(cycleStart).toISOString(), cycleEnd: new Date(cycleEnd).toISOString(),
    cycleHours: decimal(String((cycleEnd - cycleStart) / 3600000)), payment,
    leaseStart, leaseEnd, completedYears
  };
}

function leaseCosts(profile, siteId = null) {
  return (profile.assetCosts || []).filter(cost =>
    cost.included !== false && cost.costCategory === 'lease_payment' && cost.amountUnit === 'currency_per_month' &&
    (!cost.siteId || (siteId && cost.siteId === siteId)));
}

function calculateCurrentLeaseRate(profile, asOf, siteId = null) {
  if (!siteId && (profile.assetCosts || []).some(cost =>
    cost.included !== false && cost.costCategory === 'lease_payment' && cost.siteId)) {
    return { ...unavailable(`${profile.currencyCode}/hour`, REASONS.SITE_SCOPE_UNAVAILABLE), lineItems: [] };
  }
  const configured = leaseCosts(profile, siteId);
  if (!configured.length) {
    return { value: null, unit: `${profile.currencyCode}/hour`, status: 'notApplicable', reasonCodes: [], lineItems: [] };
  }
  const lines = [];
  const invalid = [];
  for (const cost of configured) {
    let cycle;
    try { cycle = leaseCycleAt(cost, asOf, profile.billingTimezone); } catch (_) { cycle = null; }
    if (!cycle) {
      const starts = parseDateParts(cost.startDate);
      if (!starts || cost.recurrence?.frequency !== 'monthly') invalid.push(cost.assetCostId);
      continue;
    }
    const rate = divide(cycle.payment, cycle.cycleHours, 15);
    lines.push({
      assetCostId: cost.assetCostId, formulaId: 'monthly-lease-payment-divided-by-local-cycle-hours-v1',
      monthlyPayment: fixed(cycle.payment, 2), cycleStart: cycle.cycleStart, cycleEnd: cycle.cycleEnd,
      cycleHours: fixed(cycle.cycleHours, 6), value: fixed(rate, 6), unit: `${profile.currencyCode}/hour`,
      marginal: false
    });
  }
  if (invalid.length) return {
    ...unavailable(`${profile.currencyCode}/hour`, REASONS.LEASE_CONFIGURATION_INVALID),
    lineItems: lines, invalidAssetCostIds: invalid
  };
  if (!lines.length) return { value: null, unit: `${profile.currencyCode}/hour`, status: 'notApplicable', reasonCodes: [], lineItems: [] };
  return {
    value: fixed(sum(lines.map(line => line.value)), 2), unit: `${profile.currencyCode}/hour`, status: 'complete', reasonCodes: [],
    formulaId: 'allocated-monthly-lease-cost-rate-v1', lineItems: lines, marginal: false
  };
}

function calculateLeaseAccrual(profile, start, end, siteId = null) {
  if (!siteId && (profile.assetCosts || []).some(cost =>
    cost.included !== false && cost.costCategory === 'lease_payment' && cost.siteId)) {
    return { ...unavailable(profile.currencyCode, REASONS.SITE_SCOPE_UNAVAILABLE), lineItems: [], denominators: [] };
  }
  const configured = leaseCosts(profile, siteId);
  if (!configured.length) return {
    value: '0.00', unit: profile.currencyCode, status: 'notApplicable', reasonCodes: [],
    formulaId: 'calendar-monthly-lease-proration-v1', lineItems: [], denominators: []
  };
  const rangeStart = Date.parse(start);
  const rangeEnd = Date.parse(end);
  const lines = [];
  const denominators = new Set();
  let exactTotal = decimal('0');
  try {
    for (const cost of configured) {
      denominators.add(cost.recurrence?.energyDenominator);
      const startParts = parseDateParts(cost.startDate);
      if (!startParts || cost.recurrence?.frequency !== 'monthly') {
        return { ...unavailable(profile.currencyCode, REASONS.LEASE_CONFIGURATION_INVALID), lineItems: lines, denominators: [...denominators] };
      }
      const leaseStart = zonedMidnightUtc(startParts.year, startParts.month, startParts.day, profile.billingTimezone);
      const endParts = parseDateParts(cost.recurrence?.endDate);
      const leaseEnd = endParts ? zonedMidnightUtc(endParts.year, endParts.month, endParts.day, profile.billingTimezone) : null;
      let cursor = Math.max(rangeStart, leaseStart);
      const final = Math.min(rangeEnd, leaseEnd === null ? rangeEnd : leaseEnd);
      let cycleCount = 0;
      while (cursor < final) {
        if (cycleCount >= 2400) throw new RangeError('Lease range exceeds supported cycle count');
        const cycle = leaseCycleAt(cost, new Date(cursor).toISOString(), profile.billingTimezone);
        if (!cycle) return { ...unavailable(profile.currencyCode, REASONS.LEASE_CONFIGURATION_INVALID), lineItems: lines, denominators: [...denominators] };
        const overlapStart = Math.max(cursor, Date.parse(cycle.cycleStart), leaseStart);
        const overlapEnd = Math.min(final, Date.parse(cycle.cycleEnd));
        if (overlapEnd <= overlapStart) throw new RangeError('Lease cycle does not advance');
        const accruedHours = decimal(String((overlapEnd - overlapStart) / 3600000));
        const accrued = divide(multiply(cycle.payment, accruedHours), cycle.cycleHours, 18);
        exactTotal = add(exactTotal, accrued);
        lines.push({
          assetCostId: cost.assetCostId, formulaId: 'monthly-payment-times-covered-hours-divided-by-cycle-hours-v1',
          cycleStart: cycle.cycleStart, cycleEnd: cycle.cycleEnd, cycleHours: fixed(cycle.cycleHours, 6),
          accruedStart: new Date(overlapStart).toISOString(), accruedEnd: new Date(overlapEnd).toISOString(),
          accruedHours: fixed(accruedHours, 6), monthlyPayment: fixed(cycle.payment, 2),
          amount: fixed(accrued, 2), energyDenominator: cost.recurrence.energyDenominator
        });
        cursor = overlapEnd;
        cycleCount += 1;
      }
    }
  } catch (_error) {
    return { ...unavailable(profile.currencyCode, REASONS.LEASE_CONFIGURATION_INVALID), lineItems: lines, denominators: [...denominators] };
  }
  return {
    value: fixed(exactTotal, 2), unit: profile.currencyCode,
    status: 'complete', reasonCodes: [], formulaId: 'calendar-monthly-lease-proration-v1',
    lineItems: lines, denominators: [...denominators]
  };
}

function timeSeconds(value) {
  if (!value) return null;
  const parts = String(value).split(':').map(Number);
  return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
}

function tariffApplies(component, instant, timezone) {
  if (!hasScheduledApplicability(component)) return true;
  const local = localClock(instant, timezone);
  if (Array.isArray(component.appliesMonths) && !component.appliesMonths.includes(local.month)) return false;
  if (Array.isArray(component.appliesWeekdays) && !component.appliesWeekdays.includes(local.weekday)) return false;
  const start = timeSeconds(component.localTimeStart);
  const end = timeSeconds(component.localTimeEnd);
  if (start === null || end === null) return true;
  return start < end ? local.seconds >= start && local.seconds < end : local.seconds >= start || local.seconds < end;
}

function hasScheduledApplicability(component) {
  return (component.localTimeStart !== null && component.localTimeStart !== undefined) ||
    (component.localTimeEnd !== null && component.localTimeEnd !== undefined) ||
    Array.isArray(component.appliesWeekdays) || Array.isArray(component.appliesMonths);
}

function applicable(profile, type, instant) {
  return (profile.tariffComponents || [])
    .filter(component => component.componentType === type && tariffApplies(component, instant, profile.billingTimezone))
    .sort((a, b) => (a.calculationOrder || 0) - (b.calculationOrder || 0));
}

function timezoneTransitions(start, end, timezone) {
  const transitions = [];
  const step = 6 * 60 * 60 * 1000;
  let left = start;
  let leftOffset = timezoneOffsetMs(left, timezone);
  for (let right = Math.min(left + step, end); left < end; right = Math.min(right + step, end)) {
    const rightOffset = timezoneOffsetMs(right, timezone);
    if (rightOffset !== leftOffset) {
      let low = left;
      let high = right;
      while (high - low > 1000) {
        const middle = Math.floor((low + high) / 2);
        if (timezoneOffsetMs(middle, timezone) === leftOffset) low = middle;
        else high = middle;
      }
      transitions.push(high);
    }
    left = right;
    leftOffset = rightOffset;
    if (right === end) break;
  }
  return transitions;
}

function uncachedTariffBucketDecision(profile, type, start, end) {
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return { error: true, component: null };
  const components = (profile.tariffComponents || []).filter(item => item.componentType === type);
  if (components.every(component => !hasScheduledApplicability(component))) return { error: components.length > 1, component: components.length === 1 ? components[0] : null };
  const boundaries = new Set([startMs, ...timezoneTransitions(startMs, endMs, profile.billingTimezone)]);
  const dates = new Map();
  for (let instant = startMs - 172800000; instant <= endMs + 172800000; instant += 43200000) {
    const local = localClock(instant, profile.billingTimezone);
    dates.set(`${local.year}-${local.month}-${local.day}`, local);
  }
  for (const local of dates.values()) {
    const seconds = new Set([0]);
    for (const component of components) {
      const componentStart = timeSeconds(component.localTimeStart);
      const componentEnd = timeSeconds(component.localTimeEnd);
      if (componentStart !== null) seconds.add(componentStart);
      if (componentEnd !== null) seconds.add(componentEnd);
    }
    for (const second of seconds) {
      for (const instant of zonedLocalUtcCandidates(local.year, local.month, local.day, second, profile.billingTimezone)) {
        if (instant > startMs && instant < endMs) boundaries.add(instant);
      }
    }
  }
  let selected;
  for (const instant of [...boundaries].sort((a, b) => a - b)) {
    const matches = applicable(profile, type, new Date(instant).toISOString());
    if (matches.length > 1) return { error: true, component: null };
    const component = matches[0] || null;
    if (selected !== undefined && component !== selected) return { error: true, component: null };
    selected = component;
  }
  return { error: false, component: selected || null };
}

const tariffDecisions = new WeakMap();
function tariffBucketDecision(profile, type, start, end) {
  const fingerprint = JSON.stringify([profile.billingTimezone, profile.tariffComponents]);
  let memo = tariffDecisions.get(profile);
  if (!memo || memo.fingerprint !== fingerprint) { memo = { fingerprint, decisions: new Map() }; tariffDecisions.set(profile, memo); }
  const key = `${type}:${start}:${end}`;
  if (!memo.decisions.has(key)) {
    if (memo.decisions.size >= 50000) memo.decisions.delete(memo.decisions.keys().next().value);
    memo.decisions.set(key, uncachedTariffBucketDecision(profile, type, start, end));
  }
  return memo.decisions.get(key);
}

function tariffForWholeBucket(profile, type, start, end) {
  const decision = tariffBucketDecision(profile, type, start, end);
  return decision.error ? null : decision.component;
}

function lineItem(component, formulaId, quantity, rate, amount, unit = 'currency') {
  return {
    tariffComponentId: component.tariffComponentId,
    componentType: component.componentType,
    componentName: component.componentName || null,
    formulaId,
    quantity: quantity === null ? null : fixed(quantity, 6),
    rate: rate === null ? null : fixed(rate, 12),
    amount: fixed(amount, 2),
    unit
  };
}

function lineage(profile) {
  return {
    profileId: profile.profileId,
    profileVersion: profile.profileVersion,
    calculationVersion: CALCULATION_VERSION,
    timezone: profile.billingTimezone,
    currency: profile.currencyCode
  };
}

function modeledGenerationCoefficient(profile, siteId = null) {
  if (!isDecimal(profile.modeledGenerationCostPerKwh)) return null;
  return { value: decimal(profile.modeledGenerationCostPerKwh), source: 'profile.modeledGenerationCostPerKwh', siteId };
}

// Prices describe a unit of energy, independently of the instantaneous power.
// Keep full decimal precision here; hourly monetary amounts are rounded separately.
function calculateEnergyPrices(profile, instant) {
  const unit = `${profile.currencyCode}/kWh`;
  const price = (type) => {
    const tariffs = applicable(profile, type, instant);
    if (tariffs.length !== 1) return unavailable(unit, tariffs.length ? REASONS.UNSUPPORTED_SCENARIO : REASONS.MISSING_TARIFF);
    if (tariffs[0].rateUnit !== 'currency_per_kwh') return unavailable(unit, REASONS.UNSUPPORTED_SCENARIO);
    let total = decimal(tariffs[0].rate);
    if (type === 'import_energy') {
      for (const rider of applicable(profile, 'rider', instant).filter(item => item.rateUnit === 'currency_per_kwh')) {
        total = add(total, rider.rate);
      }
    }
    const percentages = (profile.tariffComponents || []).filter(item => ['tax', 'rider'].includes(item.componentType)
      && ['percent', 'decimal_fraction'].includes(item.rateUnit)
      && tariffApplies(item, instant, profile.billingTimezone))
      .sort((a, b) => (a.calculationOrder || 0) - (b.calculationOrder || 0));
    let taxApplied = false;
    for (const component of percentages) {
      if (type === 'export_credit' && component.schedule?.taxBasis === 'charges_before_export_credits') continue;
      const fraction = component.rateUnit === 'percent' ? divide(component.rate, '100', 15) : decimal(component.rate);
      total = add(total, multiply(total, fraction));
      taxApplied ||= component.componentType === 'tax' && compare(fraction, '0') !== 0;
    }
    return { value: fixed(total, 12), unit, status: 'complete', reasonCodes: [],
      formulaId: 'scheduled-energy-unit-price-v1', taxApplied, excludesFixedAndDemandCharges: true };
  };
  return { importEnergyPricePerKwh: price('import_energy'), exportCreditPerKwh: price('export_credit') };
}

function calculateCurrent(profile, snapshot, options = {}) {
  const base = {
    asOf: snapshot.boundary,
    ...lineage(profile),
    ...calculateEnergyPrices(profile, snapshot.boundary),
    energyCalculationVersion: snapshot.calculationVersion,
    sourceWatermark: snapshot.sourceWatermark,
    alignment: snapshot.aligned,
    coverage: {
      status: snapshot.aligned?.alignmentStatus === 'aligned' ? 'complete' : 'unavailable',
      alignmentStatus: snapshot.aligned?.alignmentStatus || 'unavailable',
      reasonCodes: snapshot.aligned?.reasonCodes || []
    }
  };
  const allocatedLease = calculateCurrentLeaseRate(profile, snapshot.boundary, options.siteId);
  if (!snapshot.aligned) {
    const reasons = [REASONS.INPUTS_NOT_ALIGNED];
    if (allocatedLease.status === 'unavailable') reasons.push(...allocatedLease.reasonCodes);
    return {
      ...base, status: allocatedLease.status === 'complete' ? 'partial' : 'unavailable', reasonCodes: [...new Set(reasons.filter(Boolean))],
      utilityEnergyCostRate: unavailable(`${profile.currencyCode}/hour`, reasons),
      modeledGenerationCostRate: unavailable(`${profile.currencyCode}/hour`, reasons),
      allocatedLeaseCostRate: allocatedLease,
      outageEconomicValue: { ...unavailable(profile.currencyCode, REASONS.OUTAGE_NOT_SELECTED), includedInEnergyCost: false }
    };
  }
  const gridKw = snapshot.aligned.inputs.pcc?.values?.powerKw;
  const generationKw = snapshot.aligned.inputs.site?.values?.generationKw;
  const alignmentReasons = snapshot.aligned.reasonCodes || [];
  const pccUnavailable = !snapshot.aligned.inputs.pcc || snapshot.aligned.inputs.pcc.stale === true ||
    alignmentReasons.some(code => code === 'ambiguousSource:pcc' || code === 'stale:pcc');
  const siteUnavailable = !snapshot.aligned.inputs.site || snapshot.aligned.inputs.site.stale === true ||
    alignmentReasons.some(code => code === 'ambiguousSource:site' || code === 'stale:site');
  let utility;
  if (pccUnavailable || !isDecimal(gridKw)) {
    utility = unavailable(`${profile.currencyCode}/hour`, [
      REASONS.MISSING_ENERGY_INPUT,
      snapshot.aligned.inputs.pcc?.stale === true || alignmentReasons.includes('stale:pcc') ? REASONS.STALE_INPUT : null,
      alignmentReasons.find(code => code === 'ambiguousSource:pcc')
    ]);
  } else {
    const direction = compare(gridKw, '0') >= 0 ? 'import_energy' : 'export_credit';
    const tariff = applicable(profile, direction, snapshot.boundary);
    if (tariff.length !== 1) {
      utility = unavailable(`${profile.currencyCode}/hour`, tariff.length ? REASONS.UNSUPPORTED_SCENARIO : REASONS.MISSING_TARIFF);
    } else {
      const quantity = direction === 'import_energy' ? decimal(gridKw) : multiply(gridKw, '-1');
      const unsigned = multiply(quantity, tariff[0].rate);
      const amount = direction === 'export_credit' ? multiply(unsigned, '-1') : unsigned;
      utility = {
        value: fixed(amount, 2), unit: `${profile.currencyCode}/hour`, status: 'complete', reasonCodes: [],
        formulaId: direction === 'import_energy' ? 'current-import-energy-v1' : 'current-export-credit-v1',
        gridPowerKw: fixed(gridKw, 6),
        lineItems: [lineItem(tariff[0], direction === 'import_energy' ? 'import-kw-times-rate-v1' : 'negative-export-kw-times-credit-v1', quantity, tariff[0].rate, amount, `${profile.currencyCode}/hour`)]
      };
    }
  }
  if (utility.status === 'complete' && compare(gridKw, '0') >= 0) {
    const lines = [...utility.lineItems];
    for (const rider of applicable(profile, 'rider', snapshot.boundary).filter(item => item.rateUnit === 'currency_per_kwh')) {
      lines.push(lineItem(rider, 'current-import-kw-times-rider-v1', gridKw, rider.rate, multiply(gridKw, rider.rate), `${profile.currencyCode}/hour`));
    }
    let total = sum(lines.map(item => item.amount));
    for (const tax of applicable(profile, 'tax', snapshot.boundary).filter(item => item.schedule?.taxBasis === 'charges_before_export_credits'
      && ['percent', 'decimal_fraction'].includes(item.rateUnit))) {
      const fraction = tax.rateUnit === 'percent' ? divide(tax.rate, '100', 15) : decimal(tax.rate);
      const amount = multiply(total, fraction);
      lines.push(lineItem(tax, 'current-import-charges-times-tax-v1', total, fraction, amount, `${profile.currencyCode}/hour`));
      total = add(total, amount);
    }
    utility = { ...utility, value: fixed(total, 2), lineItems: lines };
  }
  const coefficient = modeledGenerationCoefficient(profile, options.siteId);
  const modeled = siteUnavailable || !isDecimal(generationKw) ? unavailable(`${profile.currencyCode}/hour`, [
    REASONS.MISSING_ENERGY_INPUT,
    snapshot.aligned.inputs.site?.stale === true || alignmentReasons.includes('stale:site') ? REASONS.STALE_INPUT : null,
    alignmentReasons.find(code => code === 'ambiguousSource:site')
  ]) :
    coefficient === null ? unavailable(`${profile.currencyCode}/hour`, REASONS.MISSING_MODELED_RATE) : {
    value: fixed(multiply(generationKw, coefficient.value), 2), unit: `${profile.currencyCode}/hour`, status: 'complete', reasonCodes: [],
    formulaId: 'modeled-generation-kw-times-lifecycle-coefficient-v1',
    generationKw: fixed(generationKw, 6), coefficient: fixed(coefficient.value, 12), coefficientUnit: `${profile.currencyCode}/kWh`,
    coefficientSource: coefficient.source, siteId: coefficient.siteId || null
  };
  const reasons = [
    ...utility.reasonCodes, ...modeled.reasonCodes,
    ...(allocatedLease.status === 'unavailable' ? allocatedLease.reasonCodes : [])
  ];
  const namedOutage = (profile.outageAssumptions || []).some(item =>
    item.enabled === true && item.scenarioName === options.scenario);
  const outage = options.scenario && options.scenario !== 'standard' ?
    { ...unavailable(profile.currencyCode, namedOutage ? REASONS.OUTAGE_INPUT_REQUIRED : REASONS.OUTAGE_SCENARIO_UNAVAILABLE), includedInEnergyCost: false, scenario: options.scenario } :
    { ...unavailable(profile.currencyCode, REASONS.OUTAGE_NOT_SELECTED), includedInEnergyCost: false, scenario: 'standard' };
  if (options.scenario && options.scenario !== 'standard') reasons.push(...outage.reasonCodes);
  return {
    ...base, status: utility.status === 'complete' || modeled.status === 'complete' || allocatedLease.status === 'complete' ?
      (reasons.length ? 'partial' : 'complete') : 'unavailable',
    reasonCodes: [...new Set(reasons)],
    utilityEnergyCostRate: utility,
    modeledGenerationCostRate: modeled,
    allocatedLeaseCostRate: allocatedLease,
    outageEconomicValue: outage
  };
}

function metricRows(rows, metric, periodKind = null) {
  return rows.filter(row => row.metric === metric && (!periodKind || row.periodKind === periodKind))
    .sort((a, b) => Date.parse(a.periodStart) - Date.parse(b.periodStart));
}

function rowsCoverRange(rows, start, end) {
  if (!start || !end || !rows.length) return false;
  const rangeStart = Date.parse(start);
  const rangeEnd = Date.parse(end);
  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd) || rangeEnd <= rangeStart) return false;
  let cursor = rangeStart;
  let covered = decimal('0');
  for (const row of rows) {
    const rowStart = Date.parse(row.periodStart);
    const rowEnd = Date.parse(row.periodEnd);
    if (!Number.isFinite(rowStart) || !Number.isFinite(rowEnd) || rowStart !== cursor || rowEnd <= rowStart || rowEnd > rangeEnd) {
      return false;
    }
    if (row.status !== 'complete' || row.value === null ||
        compare(row.coveredSeconds || '0', row.expectedSeconds || '0') !== 0) return false;
    covered = add(covered, row.coveredSeconds || '0');
    cursor = rowEnd;
  }
  const expected = decimal(String((rangeEnd - rangeStart) / 1000));
  return cursor === rangeEnd && compare(covered, expected) === 0;
}

function selectSourceSeries(rows, range) {
  const groups = new Map();
  for (const row of rows) {
    const key = row.sourceKind || 'unspecified';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  const complete = [...groups].filter(([, series]) => rowsCoverRange(series, range?.start, range?.end));
  const counter = complete.filter(([kind]) => kind === 'counter_derived' || kind === 'counterDerived');
  if (counter.length === 1) return { sourceKind: counter[0][0], rows: counter[0][1] };
  if (counter.length > 1) return { error: REASONS.AMBIGUOUS_ROLLUP };
  const estimates = complete.filter(([kind]) => kind === 'estimated' || kind === 'sampled_estimate');
  if (estimates.length === 1) return { sourceKind: estimates[0][0], rows: estimates[0][1] };
  if (estimates.length > 1) return { error: REASONS.AMBIGUOUS_ROLLUP };
  if (groups.size === 1) {
    const [sourceKind, series] = [...groups][0];
    if (['counter_derived', 'counterDerived', 'estimated', 'sampled_estimate'].includes(sourceKind)) {
      return { sourceKind, rows: series };
    }
  }
  return { error: groups.size ? REASONS.SOURCE_SERIES_UNAVAILABLE : REASONS.MISSING_ROLLUP };
}

function aggregateMetric(rows, metric, required = true, range = null, periodKind = null, expectedSourceKinds = null) {
  const metricCandidates = metricRows(rows, metric, periodKind);
  const candidates = expectedSourceKinds ?
    metricCandidates.filter(row => expectedSourceKinds.includes(row.sourceKind)) : metricCandidates;
  if (!candidates.length) return {
    value: null, rows: [], status: required ? 'unavailable' : 'notApplicable',
    reasonCodes: required ? [metricCandidates.length ? REASONS.SOURCE_SERIES_UNAVAILABLE : REASONS.MISSING_ROLLUP] : [],
    sourceLineage: null
  };
  const series = selectSourceSeries(candidates, range);
  if (series.error) return { value: null, rows: [], status: 'unavailable', reasonCodes: [series.error], sourceLineage: null };
  const selected = series.rows;
  const bucketIdentities = new Set();
  for (const row of selected) {
    // account-cost-v1 requires one authoritative rollup for each metric/bucket.
    // Multiple sites, devices, or source kinds must be resolved by the read
    // model rather than silently summed by the costing layer.
    const bucket = [row.periodStart, row.periodEnd].join('|');
    if (bucketIdentities.has(bucket)) {
      return { value: null, rows: selected, status: 'unavailable', reasonCodes: [REASONS.AMBIGUOUS_ROLLUP], sourceLineage: null };
    }
    bucketIdentities.add(bucket);
  }
  const incomplete = selected.some(row => row.status !== 'complete' || compare(row.coveredSeconds || '0', row.expectedSeconds || '0') !== 0 || row.value === null) ||
    (range && !rowsCoverRange(selected, range.start, range.end));
  return {
    value: incomplete ? null : sum(selected.map(row => row.value)), rows: selected,
    status: incomplete ? 'partial' : 'complete', reasonCodes: incomplete ? [REASONS.PARTIAL_COVERAGE] : [],
    sourceLineage: {
      metric, sourceKind: series.sourceKind,
      rollupIds: selected.map(row => row.energyRollupId), revisions: selected.map(row => row.revision)
    }
  };
}

function aggregatePreferredEnergy(rows, counterMetric, sampledMetric, required, range, periodKind) {
  const counter = aggregateMetric(rows, counterMetric, false, range, periodKind,
    ['counter_derived', 'counterDerived']);
  if (counter.status === 'complete') return counter;
  const sampled = aggregateMetric(rows, sampledMetric, required, range, periodKind,
    ['estimated', 'sampled_estimate']);
  if (sampled.status === 'complete') return sampled;
  return counter.rows.length ? { ...counter, status: 'unavailable' } : sampled;
}

function coverage(rows, start, end) {
  const expected = decimal(String((Date.parse(end) - Date.parse(start)) / 1000));
  const covered = rows.length ? sum(rows.map(row => row.coveredSeconds || '0')) : decimal('0');
  return {
    expectedSeconds: fixed(expected, 3), coveredSeconds: fixed(covered, 3),
    coveragePct: compare(expected, '0') === 0 ? null : fixed(multiply(divide(covered, expected, 9), '100'), 6),
    status: compare(expected, covered) === 0 ? 'complete' : (compare(covered, '0') > 0 ? 'partial' : 'unavailable')
  };
}

function billingStartDayMatches(component, billingStartDay) {
  const configuredBillingDay = Number(component.schedule?.billingPeriodStartDay);
  return Number.isInteger(configuredBillingDay) && configuredBillingDay >= 1 && configuredBillingDay <= 28 &&
    Number.isInteger(billingStartDay) && configuredBillingDay === billingStartDay;
}

function exactLocalBillingPeriod(profile, component, start, end, periodKind, billingStartDay) {
  const configuredBillingDay = Number(component.schedule?.billingPeriodStartDay);
  if (periodKind !== 'billing_period' || !Number.isInteger(configuredBillingDay) ||
      configuredBillingDay < 1 || configuredBillingDay > 28 ||
      !billingStartDayMatches(component, billingStartDay)) return false;
  const localStart = localClock(start, profile.billingTimezone);
  const localEnd = localClock(end, profile.billingTimezone);
  const nextMonth = calendarMonth(localStart.year, localStart.month, 1);
  return localStart.day === configuredBillingDay && localStart.seconds === 0 &&
    localEnd.year === nextMonth.year && localEnd.month === nextMonth.month &&
    localEnd.day === configuredBillingDay && localEnd.seconds === 0;
}

function periodTariffLines(profile, metrics, start, end, options = {}) {
  const lines = [];
  const reasons = [];
  const energy = [
    ['import_energy', metrics.import, 'period-import-energy-v1', false],
    ['export_credit', metrics.export, 'period-export-credit-v1', true]
  ];
  for (const [type, aggregate, formulaId, credit] of energy) {
    if (aggregate.status !== 'complete') {
      reasons.push(...aggregate.reasonCodes);
      continue;
    }
    for (const row of aggregate.rows) {
      const component = tariffForWholeBucket(profile, type, row.periodStart, row.periodEnd);
      if (!component) {
        reasons.push(REASONS.TARIFF_ELIGIBILITY_UNPROVEN);
        continue;
      }
      const raw = multiply(row.value, component.rate);
      const amount = credit ? multiply(raw, '-1') : raw;
      lines.push(lineItem(component, formulaId, row.value, component.rate, amount));
    }
  }
  const demandComponents = (profile.tariffComponents || []).filter(item => item.componentType === 'demand');
  const demandSeries = aggregateMetric(metrics.allRows, 'grid_import_demand_kw', demandComponents.length > 0,
    { start, end }, 'demand_interval');
  if (demandComponents.length) {
    if (demandSeries.status !== 'complete') reasons.push(...demandSeries.reasonCodes);
    const applicableDemandComponents = demandSeries.status === 'complete' ? demandComponents.filter(component =>
      demandSeries.rows.some(row => {
        const decision = tariffBucketDecision(profile, 'demand', row.periodStart, row.periodEnd);
        return !decision.error && decision.component === component;
      })) : demandComponents;
    if (applicableDemandComponents.some(component => !billingStartDayMatches(component, options.billingStartDay))) {
      reasons.push(REASONS.BILLING_START_DAY_MISMATCH);
    } else if (applicableDemandComponents.some(component => !exactLocalBillingPeriod(
      profile, component, start, end, options.periodKind, options.billingStartDay))) {
      reasons.push(REASONS.DEMAND_BILLING_PERIOD_UNPROVEN);
    }
    if (demandSeries.status === 'complete' &&
        !reasons.includes(REASONS.DEMAND_BILLING_PERIOD_UNPROVEN) &&
        !reasons.includes(REASONS.BILLING_START_DAY_MISMATCH)) {
      const eligibleByComponent = new Map(demandComponents.map(component => [component, []]));
      for (const row of demandSeries.rows) {
        const decision = tariffBucketDecision(profile, 'demand', row.periodStart, row.periodEnd);
        if (decision.error) {
          reasons.push(REASONS.TARIFF_ELIGIBILITY_UNPROVEN);
          continue;
        }
        if (!decision.component) continue;
        const durationMinutes = (Date.parse(row.periodEnd) - Date.parse(row.periodStart)) / 60000;
        if (Number(decision.component.demandIntervalMinutes) !== durationMinutes) {
          reasons.push(REASONS.DEMAND_INTERVAL_UNPROVEN);
          continue;
        }
        eligibleByComponent.get(decision.component)?.push(row);
      }
      if (!reasons.includes(REASONS.TARIFF_ELIGIBILITY_UNPROVEN) &&
          !reasons.includes(REASONS.DEMAND_INTERVAL_UNPROVEN)) {
        for (const component of demandComponents) {
          const eligible = eligibleByComponent.get(component) || [];
          if (!eligible.length) continue;
          const peak = eligible.reduce((best, row) => compare(row.value, best.value) > 0 ? row : best);
          lines.push(lineItem(component, 'billing-period-demand-peak-times-rate-v1', peak.value,
            component.rate, multiply(peak.value, component.rate)));
        }
      }
    }
  }
  const durationHours = divide(String((Date.parse(end) - Date.parse(start)) / 1000), '3600', 12);
  const additive = (profile.tariffComponents || []).filter(component =>
    ['fixed', 'rider'].includes(component.componentType) && !['percent', 'decimal_fraction'].includes(component.rateUnit));
  for (const component of additive) {
    if (hasScheduledApplicability(component)) {
      reasons.push(REASONS.TARIFF_ELIGIBILITY_UNPROVEN);
      continue;
    }
    if (component.rateUnit === 'currency_per_period') {
      if (!billingStartDayMatches(component, options.billingStartDay)) {
        reasons.push(REASONS.BILLING_START_DAY_MISMATCH);
        continue;
      }
      const energyRows = metrics.allRows.filter(row => row.periodKind === options.periodKind);
      const suppliedEvidence = options.billingEvidence?.status === 'complete' &&
        Date.parse(options.billingEvidence.start) === Date.parse(start) && Date.parse(options.billingEvidence.end) === Date.parse(end);
      const billingEvidence = suppliedEvidence || (options.periodKind === 'billing_period' && energyRows.length > 0 &&
        energyRows.every(row => row.periodKind === 'billing_period' &&
          Date.parse(row.periodStart) === Date.parse(start) && Date.parse(row.periodEnd) === Date.parse(end)));
      if (!billingEvidence || !exactLocalBillingPeriod(
        profile, component, start, end, options.periodKind, options.billingStartDay)) {
        reasons.push(REASONS.FIXED_PERIOD_UNPROVEN);
        continue;
      }
      lines.push(lineItem(component, 'billing-period-fixed-amount-v1', '1', component.rate, component.rate));
    } else if (component.rateUnit === 'currency_per_hour') {
      lines.push(lineItem(component, 'period-hours-times-rate-v1', durationHours, component.rate, multiply(durationHours, component.rate)));
    } else if (component.rateUnit === 'currency_per_kwh' && metrics.import.status === 'complete') {
      lines.push(lineItem(component, 'period-import-kwh-times-rider-v1', metrics.import.value, component.rate, multiply(metrics.import.value, component.rate)));
    }
  }
  let subtotal = sum(lines.map(item => item.amount));
  const percentages = (profile.tariffComponents || []).filter(component =>
    ['tax', 'rider'].includes(component.componentType) && ['percent', 'decimal_fraction'].includes(component.rateUnit));
  for (const component of percentages) {
    if (hasScheduledApplicability(component)) {
      reasons.push(REASONS.TARIFF_ELIGIBILITY_UNPROVEN);
      continue;
    }
    const fraction = component.rateUnit === 'percent' ? divide(component.rate, '100', 15) : decimal(component.rate);
    const taxBase = component.schedule?.taxBasis === 'charges_before_export_credits'
      ? sum(lines.filter(item => item.componentType !== 'export_credit').map(item => item.amount)) : subtotal;
    const amount = multiply(taxBase, fraction);
    lines.push(lineItem(component, 'period-subtotal-times-percentage-v1', taxBase, fraction, amount));
    subtotal = add(subtotal, amount);
  }
  return { lines, total: subtotal, reasonCodes: [...new Set(reasons)], demandSourceLineage: demandSeries.sourceLineage };
}

function calculateLcoe(profile, siteId = null) {
  if (!siteId && (profile.assetCosts || []).some(cost => cost.included !== false && cost.siteId)) {
    return unavailable(`${profile.currencyCode}/kWh`, REASONS.SITE_SCOPE_UNAVAILABLE);
  }
  if (leaseCosts(profile, siteId).length) {
    return unavailable(`${profile.currencyCode}/kWh`, REASONS.LEASE_LCOE_UNSUPPORTED);
  }
  if (siteId && (profile.assetCosts || []).some(cost => cost.included !== false && cost.siteId)) {
    return {
      ...unavailable(`${profile.currencyCode}/kWh`, REASONS.SITE_MODELED_GENERATION_UNAVAILABLE),
      scope: 'site', siteId
    };
  }
  if (!isDecimal(profile.modeledAnnualGenerationKwh) || !isDecimal(profile.modeledAnnualGenerationChangeRate) ||
      !Number.isInteger(profile.analysisYears) || !isDecimal(profile.discountRate)) {
    return unavailable(`${profile.currencyCode}/kWh`, REASONS.MISSING_LCOE_INPUT);
  }
  let annualGeneration = decimal(profile.modeledAnnualGenerationKwh);
  if (compare(annualGeneration, '0') <= 0) return unavailable(`${profile.currencyCode}/kWh`, REASONS.ZERO_DENOMINATOR);
  const costs = (profile.assetCosts || []).filter(cost => cost.included !== false &&
    cost.costCategory !== 'lease_payment' && (!cost.siteId || cost.siteId === siteId));
  const scopeKey = cost => [cost.siteId || '', cost.assetId || '', cost.assetClass || ''].join('|');
  const capitalByScope = new Map();
  try {
    for (const cost of costs.filter(item => item.costCategory === 'capital')) {
      capitalByScope.set(scopeKey(cost), add(capitalByScope.get(scopeKey(cost)) || '0', cost.amount));
    }
    const years = profile.analysisYears;
    const discountBase = add('1', profile.discountRate);
    const generationChangeBase = add('1', profile.modeledAnnualGenerationChangeRate);
    let discountedCosts = decimal('0');
    let discountedGeneration = decimal('0');
    let discountFactor = decimal('1');
    for (let year = 0; year <= years; year += 1) {
      if (year > 0) {
        discountFactor = multiply(discountFactor, discountBase);
        if (year > 1) annualGeneration = multiply(annualGeneration, generationChangeBase);
        discountedGeneration = add(discountedGeneration, divide(annualGeneration, discountFactor, 18));
      }
      for (const cost of costs) {
        if (cost.startDate) return unavailable(`${profile.currencyCode}/kWh`, REASONS.MISSING_LCOE_INPUT);
        const startYear = cost.startYear ??
          (['fixed_om', 'variable_om'].includes(cost.costCategory) ? 1 : 0);
        let applies = false;
        if (cost.costCategory === 'capital' || cost.costCategory === 'replacement') applies = year === startYear;
        else if (cost.costCategory === 'residual_value') applies = year === years;
        else if (['fixed_om', 'variable_om'].includes(cost.costCategory)) applies = year >= Math.max(startYear, 1);
        if (!applies) continue;
        let amount;
        if (cost.costCategory === 'residual_value' && cost.amountUnit === 'percent') {
          const capital = capitalByScope.get(scopeKey(cost));
          if (!capital) return unavailable(`${profile.currencyCode}/kWh`, REASONS.MISSING_LCOE_INPUT);
          amount = multiply(capital, divide(cost.amount, '100', 18));
        } else {
          amount = decimal(cost.amount);
          if (cost.amountUnit === 'currency_per_kwh') amount = multiply(amount, annualGeneration);
          if (cost.amountUnit === 'currency_per_kw_year') {
            if (!isDecimal(cost.recurrence?.nameplateCapacityKw)) {
              return unavailable(`${profile.currencyCode}/kWh`, REASONS.MISSING_LCOE_INPUT);
            }
            amount = multiply(amount, cost.recurrence.nameplateCapacityKw);
          }
        }
        if (!(cost.costCategory === 'residual_value' && cost.amountUnit === 'percent')) {
          const escalationBase = add('1', cost.escalationRate || profile.inflationRate || '0');
          for (let step = 0; step < Math.max(year - startYear, 0); step += 1) amount = multiply(amount, escalationBase);
        }
        const discounted = divide(amount, discountFactor, 18);
        discountedCosts = cost.costCategory === 'residual_value' ? subtract(discountedCosts, discounted) : add(discountedCosts, discounted);
      }
    }
    if (compare(discountedGeneration, '0') <= 0) return unavailable(`${profile.currencyCode}/kWh`, REASONS.ZERO_DENOMINATOR);
    return {
      value: fixed(divide(discountedCosts, discountedGeneration, 12), 6), unit: `${profile.currencyCode}/kWh`,
      status: 'complete', reasonCodes: [], formulaId: 'discounted-lifecycle-cost-divided-by-discounted-modeled-generation-v1',
      discountedCostNumerator: fixed(discountedCosts, 2), discountedGenerationDenominatorKwh: fixed(discountedGeneration, 6),
      modeledAnnualGenerationKwh: fixed(profile.modeledAnnualGenerationKwh, 6),
      modeledAnnualGenerationChangeRate: fixed(profile.modeledAnnualGenerationChangeRate, 9),
      analysisYears: years, discountRate: fixed(profile.discountRate, 9),
      scope: 'customerProfile', siteId: null
    };
  } catch (_error) {
    return unavailable(`${profile.currencyCode}/kWh`, REASONS.MISSING_LCOE_INPUT);
  }
}

function outageValue(profile, scenario, periodHours, unservedEnergy) {
  if (!scenario || scenario === 'standard') {
    return { ...unavailable(profile.currencyCode, REASONS.OUTAGE_NOT_SELECTED), includedInEnergyCost: false, scenario: 'standard' };
  }
  const enabled = (profile.outageAssumptions || []).filter(item =>
    item.enabled === true && item.scenarioName === scenario);
  if (enabled.length !== 1) {
    return { ...unavailable(profile.currencyCode, REASONS.OUTAGE_SCENARIO_UNAVAILABLE), includedInEnergyCost: false, scenario };
  }
  const selected = enabled[0];
  try {
    if (selected.valueBasis === 'per_hour') {
      if (![selected.value, selected.annualEventCount, selected.annualDurationHours, selected.avoidedFraction].every(isDecimal)) {
        return { ...unavailable(profile.currencyCode, REASONS.OUTAGE_INPUT_REQUIRED), includedInEnergyCost: false, scenario };
      }
      const annual = multiply(multiply(multiply(selected.value, selected.annualEventCount), selected.annualDurationHours), selected.avoidedFraction);
      const period = multiply(annual, divide(periodHours, '8760', 15));
      return {
        value: fixed(period, 2), unit: profile.currencyCode, status: 'complete', reasonCodes: [],
        formulaId: 'selected-outage-hours-value-prorated-v1', includedInEnergyCost: false, scenario,
        annualAssumptionValue: fixed(annual, 2), outageAssumptionId: selected.outageAssumptionId
      };
    }
    if (selected.valueBasis === 'per_unserved_kwh') {
      if (unservedEnergy?.status !== 'complete' || !isDecimal(selected.value) || !isDecimal(selected.avoidedFraction)) {
        return { ...unavailable(profile.currencyCode, REASONS.UNSERVED_ENERGY_UNAVAILABLE), includedInEnergyCost: false, scenario };
      }
      const amount = multiply(multiply(unservedEnergy.value, selected.value), selected.avoidedFraction);
      return {
        value: fixed(amount, 2), unit: profile.currencyCode, status: 'complete', reasonCodes: [],
        formulaId: 'selected-unserved-kwh-times-value-times-avoided-fraction-v1', includedInEnergyCost: false, scenario,
        outageAssumptionId: selected.outageAssumptionId,
        unservedEnergyKwh: fixed(unservedEnergy.value, 6), sourceLineage: unservedEnergy.sourceLineage
      };
    }
  } catch (_error) {
    return { ...unavailable(profile.currencyCode, REASONS.OUTAGE_INPUT_REQUIRED), includedInEnergyCost: false, scenario };
  }
  return { ...unavailable(profile.currencyCode, REASONS.OUTAGE_INPUT_REQUIRED), includedInEnergyCost: false, scenario };
}

function calculateSummary(profile, pinned, range, options = {}) {
  const allRows = pinned.rows || [];
  const energyPeriodKind = options.energyPeriodKind || options.periodKind || 'hour';
  const metrics = {
    allRows,
    import: aggregatePreferredEnergy(allRows, 'grid_import_counter_kwh', 'grid_import_kwh', true, range, energyPeriodKind),
    export: aggregatePreferredEnergy(allRows, 'grid_export_counter_kwh', 'grid_export_kwh', true, range, energyPeriodKind),
    load: aggregatePreferredEnergy(allRows, 'load_counter_kwh', 'load_kwh', true, range, energyPeriodKind),
    generation: aggregatePreferredEnergy(allRows, 'solar_production_counter_kwh', 'generation_kwh', false, range, energyPeriodKind),
    unserved: aggregateMetric(allRows, 'unserved_load_kwh', false, range, energyPeriodKind,
      ['estimated', 'sampled_estimate'])
  };
  const tariffs = periodTariffLines(profile, metrics, range.start, range.end, options);
  const utilityStatus = tariffs.reasonCodes.length ? 'unavailable' : 'complete';
  const periodUtilityCost = utilityStatus === 'complete' ? {
    value: fixed(tariffs.total, 2), unit: profile.currencyCode, status: 'complete', reasonCodes: [],
    formulaId: 'period-utility-cost-v1', lineItems: tariffs.lines
  } : { ...unavailable(profile.currencyCode, tariffs.reasonCodes), formulaId: 'period-utility-cost-v1', lineItems: tariffs.lines };
  const lease = calculateLeaseAccrual(profile, range.start, range.end, options.siteId);
  const periodCustomerCost = utilityStatus !== 'complete' ?
    { ...unavailable(profile.currencyCode, tariffs.reasonCodes), formulaId: 'period-utility-plus-lease-cost-v1' } :
    lease.status === 'unavailable' ?
      { ...unavailable(profile.currencyCode, lease.reasonCodes), formulaId: 'period-utility-plus-lease-cost-v1' } : {
        value: fixed(add(tariffs.total, lease.value), 2), unit: profile.currencyCode, status: 'complete', reasonCodes: [],
        formulaId: 'period-utility-plus-lease-cost-v1',
        components: { periodUtilityCost: fixed(tariffs.total, 2), periodLeaseCost: lease.value }
      };
  let averageUtility;
  if (utilityStatus !== 'complete') averageUtility = unavailable(`${profile.currencyCode}/kWh`, tariffs.reasonCodes);
  else if (metrics.load.status !== 'complete') averageUtility = unavailable(`${profile.currencyCode}/kWh`, metrics.load.reasonCodes);
  else if (compare(metrics.load.value, '0') === 0) averageUtility = unavailable(`${profile.currencyCode}/kWh`, REASONS.ZERO_DENOMINATOR);
  else averageUtility = {
    value: fixed(divide(tariffs.total, metrics.load.value, 12), 6), unit: `${profile.currencyCode}/kWh`, status: 'complete', reasonCodes: [],
    formulaId: 'period-utility-cost-divided-by-served-load-energy-v1',
    numerator: { name: 'periodUtilityCost', value: fixed(tariffs.total, 2), unit: profile.currencyCode },
    denominator: { name: 'coveredServedLoadEnergy', value: fixed(metrics.load.value, 6), unit: 'kWh' }
  };
  let average;
  if (periodCustomerCost.status !== 'complete') average = unavailable(`${profile.currencyCode}/kWh`, periodCustomerCost.reasonCodes);
  else if (metrics.load.status !== 'complete') average = unavailable(`${profile.currencyCode}/kWh`, metrics.load.reasonCodes);
  else if (compare(metrics.load.value, '0') === 0) average = unavailable(`${profile.currencyCode}/kWh`, REASONS.ZERO_DENOMINATOR);
  else average = {
    value: fixed(divide(periodCustomerCost.value, metrics.load.value, 12), 6), unit: `${profile.currencyCode}/kWh`, status: 'complete', reasonCodes: [],
    formulaId: 'period-customer-cost-divided-by-served-load-energy-v1',
    numerator: { name: 'periodCustomerCost', value: periodCustomerCost.value, unit: profile.currencyCode },
    denominator: { name: 'coveredServedLoadEnergy', value: fixed(metrics.load.value, 6), unit: 'kWh' }
  };
  let averageLease;
  if (lease.status === 'notApplicable' || lease.lineItems.length === 0) {
    averageLease = { value: null, unit: `${profile.currencyCode}/kWh`, status: 'notApplicable', reasonCodes: [] };
  } else if (lease.status === 'unavailable') {
    averageLease = unavailable(`${profile.currencyCode}/kWh`, lease.reasonCodes);
  } else if (lease.denominators.length !== 1) {
    averageLease = unavailable(`${profile.currencyCode}/kWh`, REASONS.LEASE_DENOMINATOR_CONFLICT);
  } else {
    const denominatorName = lease.denominators[0];
    const denominatorMetric = denominatorName === 'generated_kwh' ? metrics.generation : metrics.load;
    if (!denominatorMetric || denominatorMetric.status !== 'complete' || denominatorMetric.value === null) {
      averageLease = unavailable(`${profile.currencyCode}/kWh`, REASONS.LEASE_DENOMINATOR_UNAVAILABLE);
    } else if (compare(denominatorMetric.value, '0') === 0) {
      averageLease = unavailable(`${profile.currencyCode}/kWh`, REASONS.ZERO_DENOMINATOR);
    } else {
      averageLease = {
        value: fixed(divide(lease.value, denominatorMetric.value, 12), 6), unit: `${profile.currencyCode}/kWh`,
        status: 'complete', reasonCodes: [], formulaId: 'accrued-lease-cost-divided-by-selected-energy-v1',
        numerator: { name: 'accruedLeaseCost', value: lease.value, unit: profile.currencyCode },
        denominator: { name: denominatorName, value: fixed(denominatorMetric.value, 6), unit: 'kWh' }
      };
    }
  }
  const durationHours = divide(String((Date.parse(range.end) - Date.parse(range.start)) / 1000), '3600', 12);
  const outage = outageValue(profile, options.scenario, durationHours, metrics.unserved);
  const modeledLcoe = calculateLcoe(profile, options.siteId);
  const selectedOutageReasons = options.scenario && options.scenario !== 'standard' ? outage.reasonCodes : [];
  const reasonCodes = [...new Set([
    ...periodUtilityCost.reasonCodes, ...periodCustomerCost.reasonCodes,
    ...averageUtility.reasonCodes, ...average.reasonCodes, ...modeledLcoe.reasonCodes,
    ...(lease.status === 'unavailable' ? lease.reasonCodes : []),
    ...(averageLease.status === 'unavailable' ? averageLease.reasonCodes : []),
    ...selectedOutageReasons
  ])];
  return {
    status: reasonCodes.length ? 'partial' : 'complete', reasonCodes,
    range, ...lineage(profile), energyCalculationVersion: pinned.calculationVersion,
    revisions: pinned.revisions, sourceWatermarks: pinned.sourceWatermarks,
    pin: pinned.pin || null,
    selectionLineage: pinned.pin?.selection || null,
    coverage: coverage(metrics.load.rows, range.start, range.end),
    sourceLineage: {
      gridImport: metrics.import.sourceLineage,
      gridExport: metrics.export.sourceLineage,
      servedLoad: metrics.load.sourceLineage,
      generation: metrics.generation.sourceLineage,
      unservedLoad: metrics.unserved.sourceLineage,
      demand: tariffs.demandSourceLineage
    },
    energy: {
      gridImportKwh: metrics.import.value === null ? null : fixed(metrics.import.value, 6),
      gridExportKwh: metrics.export.value === null ? null : fixed(metrics.export.value, 6),
      servedLoadKwh: metrics.load.value === null ? null : fixed(metrics.load.value, 6),
      generationKwh: metrics.generation.value === null ? null : fixed(metrics.generation.value, 6)
    },
    periodUtilityCost,
    periodLeaseCost: lease,
    periodCustomerCost,
    averageUtilityEnergyCost: averageUtility,
    averageCustomerEnergyCost: average,
    averageLeaseEnergyCost: averageLease,
    modeledLcoe,
    outageEconomicValue: outage,
    outageIncludedInEnergyCost: false
  };
}

export {
  CALCULATION_VERSION,
  REASONS,
  add,
  aggregateMetric,
  applicable,
  calculateCurrent,
  calculateCurrentLeaseRate,
  calculateLeaseAccrual,
  calculateLcoe,
  calculateSummary,
  compare,
  decimal,
  divide,
  fixed,
  leaseCycleAt,
  localClock,
  zonedMidnightUtc,
  modeledGenerationCoefficient,
  multiply,
  subtract,
  tariffApplies
};
